<template>
  <div>
    <pbas-data-table
      ref="table"
      :dataProvider="provider"
      identifier="adminFeesTable"
      @error="error = $event"
      :showSelect="false"
      :rowActions="rowActions"
      :floatActions="floatActions"
      :sortBy="'BillingTerm/EndDate'"
      :sortDescending="true"
    ></pbas-data-table>
    <error-handler :error="error"></error-handler>
    <v-snackbar
      v-model="snackbar"
      :color="color"
      :multi-line="mode === 'multi-line'"
      :timeout="timeout"
      :vertical="mode === 'vertical'"
    >
      {{ $t(message) }}
      <v-btn dark text @click="snackbar = false">
        {{ $t("globals.close") }}
      </v-btn>
    </v-snackbar>
    <!-- AdminFeeForm -->
    <v-dialog scrollable v-model="dialogs.edit" persistent max-width="1200">
      <material-card
        :title="$t(adminFeeFormTitle)"
        :color="formColor"
        :icon="adminFeeFormIcon"
      >
        <admin-fee-form
          ref="AdminFeeForm"
          :adminFee="adminFee"
          @close="
            dialogs.edit = false;
            adminFee = null;
            isNew = false;
          "
          @add="add"
          @update="update"
          :saving="saving"
        />
      </material-card>
    </v-dialog>

    <!-- Delete Warning dialog -->
    <v-dialog scrollable v-model="dialogs.delete" persistent max-width="600">
      <material-card
        v-if="adminFee !== null && adminFee !== undefined"
        :title="
          $t(
            'components.balancepayments.adminfeestable.delete_warning_title_{0}',
            [`$${adminFee.Amount}`]
          )
        "
        :color="$vuetify.theme.themes.light.error"
        icon="delete"
      >
        <v-card-text>
          {{
            $t(
              "globals.are_you_sure"
            )
          }}
        </v-card-text>
        <v-card-actions>
          <v-btn
            text
            @click="
              dialogs.delete = false;
              adminFee = null;
            "
            :loading="saving"
          >
            {{ $t("globals.cancel") }}
          </v-btn>
          <v-btn color="error" :loading="saving" @click="tryDelete">
            {{ $t("globals.delete") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import ErrorHandler from "../ErrorHandler.vue";
import { ODataProvider } from "pbas-vue-components";
import { locker } from "../../mixins/locker";
import { planSetter } from "../../mixins/planSetter";
import AdminFeesHeaders from "../../headers/AdminFees";
import AdminFeeForm from "./AdminFeeForm.vue";

export default {
  components: {
    ErrorHandler,
    AdminFeeForm,
  },
  mixins: [locker, planSetter],
  data() {
    return {
      snackbar: false,
      color: "",
      mode: "",
      timeout: 2500,
      message: "",
      stores: [],
      insuranceCarriers: [],
      trustFunds: [],
      loading: false,
      saving: false,
      error: null,
      provider: undefined,
      headers: AdminFeesHeaders,
      adminFee: null,
      isNew: false,
      rowActions: [
        {
          name: "edit",
          icon: "edit",
          title: "globals.edit",
          visible: () => this.selectedPlan.Current.HasAdminFees,
          handler: (item) => {
            this.dialogs.edit = true;
            this.isNew = false;
            this.adminFee = JSON.parse(JSON.stringify(item));
          },
        },
        {
          name: "delete",
          icon: "delete",
          title: "globals.delete",
          visible: () => this.selectedPlan.Current.HasAdminFees,
          handler: (item) => {
            this.adminFee = JSON.parse(JSON.stringify(item));
            this.dialogs.delete = true;
          },
        },
      ],
      floatActions: [
        {
          text: "globals.add",
          color: this.$vuetify.theme.themes.light.success,
          name: "add",
          icon: "add",
          handler: () => {
            this.adminFee = {};
            this.dialogs.edit = true;
            this.isNew = true;
          },
        },
      ],
      dialogs: {
        edit: false,
        delete: false,
        add: false,
      },
    };
  },

  computed: {
    ...mapGetters("common", ["language"]),
    ...mapGetters("common", {
      selectedPlan: "plan",
    }),
    ...mapGetters("plansMembers", {
      columnsDropDownOptions: "options",
      planIdentity: "planIdentity",
    }),
    adminFeeFormTitle() {
      return this.isNew
        ? "components.balancepayments.adminfeestable.add_title"
        : "components.balancepayments.adminfeestable.edit_title";
    },
    adminFeeFormIcon() {
      return this.isNew ? "add" : "edit";
    },
    formColor() {
      return this.isNew ? this.$vuetify.theme.themes.light.success : this.$vuetify.theme.themes.light.primary;
    },
  },

  watch: {
    selectedPlan: {
      handler() {
        this.loadPlanData();
      },
    },
  },

  mounted() {
    if (this.selectedPlan !== null && this.selectedPlan !== undefined) {
      this.loadPlanData();
      console.log("this plan", this.plan);
    }
  },

  methods: {
    ...mapActions("plansMembers", [
      "newExpense",
      "updateExpense",
      "deleteExpense",
      "newAdminFee",
      "updateAdminFee",
      "deleteAdminFee",
    ]),

    async loadPlanData() {
      console.log("loading plan");
      const url = `plans/${this.selectedPlan.Id}/admin-fees`;
      this.provider = new ODataProvider(this.$axiosHttp, url, this.headers);
    },

    async update(adminFee) {
      this.saving = true;
      let item = this.makeAdminFeePayload(adminFee);
      console.log("updating", item);

      try {
        await this.updateAdminFee({
          planIdentityId: this.selectedPlan.Id,
          adminFeeId: item.Id,
          payload: item,
        });
        this.successActions();
      } catch (error) {
        this.error = error;
      } finally {
        this.saving = false;
      }
    },

    async add(adminFee) {
      this.saving = true;
      let item = this.makeAdminFeePayload(adminFee);
      console.log("saving", item);

      try {
        await this.newAdminFee({
          planIdentityId: this.selectedPlan.Id,
          payload: item,
        });
        this.successActions();
      } catch (error) {
        this.error = error;
      } finally {
        this.saving = false;
      }
    },

    async tryDelete() {
      console.log("delete");
      this.saving = true;
      try {
        await this.deleteAdminFee({
          planIdentityId: this.selectedPlan.Id,
          adminFeeId: this.adminFee.Id,
        });
        this.successActions();
      } catch (error) {
        this.error = error;
      } finally {
        this.saving = false;
      }
    },

    makeAdminFeePayload(adminFee) {
      let item = JSON.parse(JSON.stringify(adminFee));
      item.BillingTermId = item.BillingTerm.Id;
      delete item.BillingTerm;
      delete item.Total;
      item.PlanId = this.selectedPlan.Id;
      item.ClientId = this.selectedPlan.ClientId;
      item.Amount = parseFloat(item.Amount);
      item.PPTax = parseFloat(item.PPTax);
      item.HSTax = parseFloat(item.HSTax);
      item.PSTax = parseFloat(item.PSTax);
      item.RSTax = parseFloat(item.RSTax);
      item.QSTax = parseFloat(item.QSTax);
      item.GSTax = parseFloat(item.GSTax);
      return item;
    },

    successActions() {
      this.color = "success";
      this.message = "globals.success";
      this.snackbar = true;
      this.dialogs.add = false;
      this.dialogs.edit = false;
      this.dialogs.delete = false;
      this.$refs.table.getDataAsync();
      this.adminFee = null;
      this.isNew = false;
    },
  },
};
</script>
